/**
 * ChatDebugPanel - Side panel for the chat view with two modes:
 *
 * - **Progress** (default): Shows reasoning run history chronologically,
 *   matching the order of chat responses. Clicking a run highlights the
 *   associated message in the conversation.
 *
 * - **Events**: Raw SessionEvent[] stream viewer for debugging the
 *   event-driven reasoning system. Filterable, collapsible, auto-scrolling.
 */

import { useState, useRef, useEffect } from 'react';
import type { AgentSession } from '../../types/session-types';
import type { SessionContextSummary } from '../../types/context-types';
import type { ProgressTree, SessionEvent } from '@localagent/sdk';
import { SessionContextPanel } from './SessionContextPanel';
import { ReasoningRunsSection } from './ReasoningRunsSection';
import { turnPending } from './live-run';

export type PanelMode = 'progress' | 'events';

interface ChatDebugPanelProps {
  session: AgentSession;
  onClose?: () => void;
  onPopOut?: () => void;
  reasoningRuns?: ProgressTree[];
  isReasoning?: boolean;
  selectedRunIndex?: number;
  onSelectRun?: (runIndex: number) => void;
  sessionId?: string;
  sessionContext?: SessionContextSummary | null;
  pinChangeCounter?: number;
  events?: SessionEvent[];
  /** Approximate character size of the assembled session context / message window,
   *  surfaced (as a token estimate) in each run's AgentConfigPanel. */
  sessionContextChars?: number;
  /** Controlled Progress/Events selection. When provided, the parent owns the
   *  mode (the unified chat dock renders a shared Files|Progress|Events tab bar)
   *  and this panel's own ModeToggle header is suppressed. Omit for the
   *  standalone story and the pop-out window, which stay self-contained. */
  mode?: PanelMode;
  onModeChange?: (m: PanelMode) => void;
  /** Render the panel's own header (mode toggle + pop-out/close buttons) and its
   *  left border. Default true. The unified dock sets this false and supplies its
   *  own chrome so the two panels share one tab bar. */
  showChrome?: boolean;
}

// ---------------------------------------------------------------------------
// Mode toggle
// ---------------------------------------------------------------------------

function ModeToggle({ mode, onChange }: { mode: PanelMode; onChange: (m: PanelMode) => void }) {
  const modes: { value: PanelMode; label: string }[] = [
    { value: 'progress', label: 'Progress' },
    { value: 'events', label: 'Events' },
  ];
  return (
    <div className="flex rounded-md bg-muted text-[10px] font-medium overflow-hidden">
      {modes.map(m => (
        <button
          key={m.value}
          data-testid={`debug-tab-${m.value}`}
          className={`px-2.5 py-1 transition-colors ${mode === m.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          onClick={() => onChange(m.value)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Raw events viewer
// ---------------------------------------------------------------------------

const eventTypeColors: Record<string, string> = {
  user_message: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  assistant_message: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  llm_call: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  tool_call_start: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  tool_call_result: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  reasoning_started: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  reasoning_completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  reasoning_failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  execution_stopped: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  execution_redirected: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  context_update: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200',
};

function truncatePreview(s: string | undefined | null, max: number): string {
  if (!s) return '';
  const line = s.split('\n')[0] ?? '';
  return line.length > max ? line.slice(0, max) + '...' : line;
}

function getEventPreview(event: SessionEvent): string {
  const d = event.data as Record<string, unknown>;
  switch (event.event_type) {
    case 'user_message':
    case 'assistant_message':
      return truncatePreview(d.content as string, 80);
    case 'reasoning_started':
      return truncatePreview(d.user_prompt as string, 60);
    case 'reasoning_completed':
      return `${d.total_iterations} iters · ${d.graphs_executed} graphs`;
    case 'reasoning_failed':
      return truncatePreview(d.error as string, 80);
    case 'state_initialized': {
      const cfg = d.agent_config as Record<string, unknown> | undefined;
      const model = (cfg?.model_name as string) ?? 'unknown';
      const tools = d.tool_names as unknown[] | undefined;
      return `${model} · ${tools?.length ?? 0} tools`;
    }
    case 'llm_call': {
      const mode = d.has_tool_calls ? 'tools' : 'text';
      return `${d.tokens} tok · iter ${d.iteration} · ${mode}`;
    }
    case 'tool_call_start':
      return `${d.tool_name}${d.args_preview ? ' ' + truncatePreview(d.args_preview as string, 50) : ''}`;
    case 'tool_call_result': {
      const preview = d.result_preview ? ' ' + truncatePreview(d.result_preview as string, 50) : '';
      return `${d.tool_name} · ${d.status}${preview}`;
    }
    case 'context_update': {
      const pct = typeof d.usage_percent === 'number' ? `${d.usage_percent.toFixed(0)}%` : '';
      const overflow = d.overflow_handled ? ' · overflow' : '';
      return `${pct} · ${d.message_count} msgs${overflow}`;
    }
    case 'execution_stopped':
    case 'execution_redirected':
      return String(d.reason ?? '');
    case 'clarification_request':
      return truncatePreview(d.question as string, 60);
    case 'clarification_response':
      return truncatePreview(d.answer_text as string, 60);
    case 'plan_created':
      return `${d.step_count} steps · ${truncatePreview(d.goal as string, 50)}`;
    case 'task_started': {
      const label = ((d.label ?? d.cognitive_tool_type ?? d.node_id ?? '') as string);
      const depth = typeof d.depth === 'number' && d.depth > 0 ? ` d${d.depth}` : '';
      return `${label}${depth}`;
    }
    case 'task_completed': {
      const summary = truncatePreview((d.conclusion_summary ?? '') as string, 50);
      const tok = typeof d.token_count === 'number' ? ` · ${d.token_count} tok` : '';
      return `${summary}${tok}`;
    }
    case 'task_failed':
      return truncatePreview((d.error ?? d.cognitive_tool_type ?? '') as string, 60);
    default:
      return '';
  }
}

function RawEventsViewer({ events }: { events: SessionEvent[] }) {
  const [filter, setFilter] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(events.length);

  useEffect(() => {
    if (autoScroll && events.length > prevCountRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevCountRef.current = events.length;
  }, [events.length, autoScroll]);

  const filtered = filter
    ? events.filter(e => e.event_type.includes(filter.toLowerCase()))
    : events;

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full gap-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Filter by event type..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="flex-1 text-[11px] px-2 py-1 rounded bg-muted focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <button
          onClick={() => setAutoScroll(!autoScroll)}
          className={`text-[10px] px-2 py-1 rounded transition-colors whitespace-nowrap ${autoScroll ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'}`}
          title={autoScroll ? 'Auto-scroll enabled' : 'Auto-scroll disabled'}
        >
          {autoScroll ? 'Auto' : 'Manual'}
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-0.5">
        {filtered.length === 0 ? (
          <p className="text-[10px] text-muted-foreground text-center py-4">
            {events.length === 0 ? 'No events yet' : 'No matching events'}
          </p>
        ) : (
          filtered.map(event => {
            const isExpanded = expandedIds.has(event.id);
            const colorClass = eventTypeColors[event.event_type] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
            const time = new Date(event.created_at).toLocaleTimeString();

            return (
              <div key={event.id} className="rounded">
                <button
                  className="w-full px-2 py-1 text-left text-[10px] hover:bg-accent/50 flex items-center gap-1.5"
                  onClick={() => toggleExpanded(event.id)}
                >
                  <span className="text-muted-foreground font-mono shrink-0">#{event.sequence_num}</span>
                  <span className={`px-1 py-0.5 rounded font-medium shrink-0 ${colorClass}`}>
                    {event.event_type}
                  </span>
                  {(() => {
                    const preview = getEventPreview(event);
                    return preview ? (
                      <span className="truncate text-muted-foreground font-mono min-w-0">{preview}</span>
                    ) : null;
                  })()}
                  <span className="text-muted-foreground ml-auto shrink-0">{time}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
                {isExpanded && (
                  <pre className="px-2 py-1.5 text-[10px] font-mono bg-muted overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(event.data, null, 2)}
                  </pre>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function ChatDebugPanel({
  session,
  onClose,
  onPopOut,
  reasoningRuns,
  isReasoning = false,
  selectedRunIndex,
  onSelectRun,
  sessionId,
  sessionContext,
  pinChangeCounter,
  events,
  sessionContextChars,
  mode: controlledMode,
  onModeChange,
  showChrome = true,
}: ChatDebugPanelProps) {
  // Controlled-or-uncontrolled: the unified dock passes `mode`/`onModeChange`;
  // the standalone story and pop-out window fall back to internal state.
  const [internalMode, setInternalMode] = useState<PanelMode>('progress');
  const mode = controlledMode ?? internalMode;
  const setMode = onModeChange ?? setInternalMode;

  return (
    <div className={`h-full bg-background flex flex-col${showChrome ? ' border-l border-border' : ''}`}>
      {/* Header: toggle + actions (suppressed when the dock owns the tab bar) */}
      {showChrome && (
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <ModeToggle mode={mode} onChange={setMode} />
        <div className="flex items-center gap-1">
          {onPopOut && (
            <button
              onClick={onPopOut}
              className="rounded-md p-1 hover:bg-accent transition-colors"
              title="Pop out to separate window"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" /><path d="m21 3-9 9" /><path d="M15 3h6v6" />
              </svg>
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className="rounded-md p-1 hover:bg-accent transition-colors"
              title="Pop back in"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
      )}

      {/* Content — a flex column so, in progress mode, the run tree scrolls in
          its own region while the Context summary stays pinned as a footer
          (min-h-0 lets the scroll region shrink below its content height). */}
      <div className="flex-1 min-h-0 flex flex-col">
        {mode === 'events' ? (
          <div className="flex-1 min-h-0 p-3">
            <RawEventsViewer events={events ?? []} />
          </div>
        ) : (
          <>
            {/* Progress tracker — owns the scroll */}
            <div data-testid="progress-scroll" className="flex-1 min-h-0 overflow-y-auto p-3">
              <ReasoningRunsSection
                runs={reasoningRuns ?? []}
                pendingTurn={turnPending(reasoningRuns ?? [], isReasoning)}
                selectedRunIndex={selectedRunIndex}
                onSelectRun={onSelectRun}
                sessionContextChars={sessionContextChars}
                events={events}
                internalAgentVisibility={session.internal_agent_visibility ?? 'friendly'}
              />
            </div>

            {/* Context section — pinned footer; capped so it can't eat the
                panel, with its own scroll when the summary is tall */}
            <div data-testid="context-footer" className="shrink-0 border-t border-border p-3 max-h-[50%] overflow-y-auto">
              <h3 className="text-xs font-semibold mb-3">Context</h3>
              <SessionContextPanel
                session={session}
                sessionId={sessionId}
                sessionContext={sessionContext ?? null}
                pinChangeCounter={pinChangeCounter}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
